import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Send, CheckCircle2, ShieldCheck, PhoneCall, Clock, Sparkles } from "lucide-react";
import PhoneWithCountry, { isPhoneValid } from "@/components/forms/PhoneWithCountry";
import { trackConversion, pushDL } from "@/lib/analytics";

export default function ConsultationForm({ copy }) {
  const { t, isRTL, lang, lh } = useLang();
  const c = (key) => (copy && copy[key]) || t(`consultation.${key}`);
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [agreed, setAgreed] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  // Progress ring: 3 checkpoints (name, phone valid, terms agreed).
  const progress = useMemo(() => {
    let p = 0;
    if (form.full_name.trim()) p += 1;
    if (isPhoneValid(form.phone)) p += 1;
    if (agreed) p += 1;
    return p / 3;
  }, [form.full_name, form.phone, agreed]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error(isRTL ? "أدخل اسمك الكامل." : "Please enter your full name."); return; }
    if (!isPhoneValid(form.phone)) { toast.error(isRTL ? "أدخل رقم هاتف صحيح (8 أرقام على الأقل)." : "Please enter a valid phone (min 8 digits)."); return; }
    if (!agreed) { toast.error(t("consultation.agree_error")); return; }
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "consultation" });
      trackConversion("consultation");
      pushDL("form_submit", { form_name: "consultation", form_id: "consultation-form" });
      pushDL("generate_lead", { form_name: "consultation" });
      setDone(true);
      toast.success(t("consultation.success"));
    } catch {
      toast.error(isRTL ? "حدث خطأ. يُرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="consultation" className="section relative overflow-hidden">
      <div className="blob-gold w-[500px] h-[500px] top-0 -end-40 opacity-70" />
      <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-16 items-center relative">
        <div className="min-w-0">
          <p className="font-mono-brand text-propertic-gold mb-4">{c("eyebrow")}</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-editorial text-2xl sm:text-3xl leading-[1.15] text-propertic-ink break-words"
          >
            {c("title_pre")}
            <br />
            <span className="font-italic text-propertic-gold">{c("title_post")}</span>
          </motion.h2>
          <p className="mt-6 text-propertic-ink-2/95 text-lg max-w-md leading-[1.7]">{c("subtitle")}</p>

          <div className="mt-10 space-y-5">
            {[
              { icon: PhoneCall, text: c("bullet_1") },
              { icon: ShieldCheck, text: c("bullet_2") },
              { icon: Clock, text: c("bullet_3") },
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-4 text-propertic-ink-2/95 text-[15px]">
                <div className="w-11 h-11 rounded-2xl bg-propertic-sand grid place-items-center text-propertic-gold flex-shrink-0">
                  <line.icon className="w-4 h-4" strokeWidth={1.6} />
                </div>
                <span className="pt-2.5">{line.text}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="card-soft rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-7 lg:p-8 lg:max-w-[480px] lg:ms-auto w-full min-w-0"
        >
          {done ? (
            <div className="text-center py-8" data-testid="consultation-success">
              {/* Boarding-pass success card, memorable finish, not a plain checkmark. */}
              <div className="boarding-pass rounded-2xl p-6 relative overflow-hidden">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-propertic-navy grid place-items-center shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-propertic-gold-2" />
                  </div>
                  <div className="text-start flex-1 min-w-0">
                    <p className="font-mono-brand text-propertic-gold text-[9px] tracking-[0.24em] uppercase mb-1">{isRTL ? "استشارة محجوزة" : "Consultation booked"}</p>
                    <p className="font-editorial text-lg text-propertic-navy leading-tight">{form.full_name || (isRTL ? "عميل بروبيرتك" : "Propertic client")}</p>
                    <p className="text-[11px] text-propertic-muted mt-1">{isRTL ? "بروبيرتك من بروفست · ريرا #٤٣٩٥٠" : "The Propertic by Provest · RERA #43950"}</p>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-dashed border-propertic-gold/40 grid grid-cols-2 gap-3 text-start">
                  <div>
                    <p className="font-mono-brand text-propertic-muted text-[9px] tracking-[0.18em] uppercase">{isRTL ? "التواصل" : "Callback"}</p>
                    <p className="font-editorial text-sm text-propertic-navy mt-0.5">{isRTL ? "خلال 24 ساعة" : "Within 24 hours"}</p>
                  </div>
                  <div>
                    <p className="font-mono-brand text-propertic-muted text-[9px] tracking-[0.18em] uppercase">{isRTL ? "بواسطة" : "By"}</p>
                    <p className="font-editorial text-sm text-propertic-navy mt-0.5">{isRTL ? "مستشار مرخّص" : "Licensed advisor"}</p>
                  </div>
                </div>
              </div>
              <p className="mt-5 text-[13px] text-propertic-ink-2/95">{t("consultation.success")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="consultation-form">
              {/* Advisor preview strip, humanises the form without inventing
                  a personal identity. Reinforces trust before the ask. */}
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-propertic-sand/60 border border-propertic-gold/25">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-propertic-gold-2 to-propertic-gold grid place-items-center shrink-0 shadow-inner">
                  <Sparkles className="w-4 h-4 text-propertic-navy" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-editorial text-[13px] sm:text-sm text-propertic-navy leading-tight">
                    {isRTL ? "مستشار بروفست المرخّص سيتواصل معك" : "A Provest licensed advisor will call you back"}
                  </p>
                  <p className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.12em] sm:tracking-[0.14em] mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    {isRTL ? "عادةً خلال 24 ساعة · ريرا #٤٣٩٥٠" : "usually within 24 hours · RERA #43950"}
                  </p>
                </div>
              </div>

              <label className="block">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.2em] uppercase">{t("consultation.full_name")} *</span>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={update("full_name")}
                  data-testid="cf-name"
                  placeholder={t("consultation.full_name")}
                  className="mt-2 w-full bg-transparent border-0 border-b border-propertic-ink/20 rounded-none px-0 h-11 text-propertic-ink text-[15px] focus:border-propertic-gold outline-none transition-colors placeholder:text-propertic-muted/60"
                />
              </label>
              <PhoneWithCountry label={`${t("consultation.phone")} *`} testid="cf-phone" value={form.phone} onChange={update("phone")} required />
              <p className="text-[11px] text-propertic-muted/90 leading-relaxed -mt-2">
                <ShieldCheck className="inline w-3 h-3 me-1 text-propertic-gold" />
                {isRTL ? "لا نتصل مرّتين، ولا نشارك رقمك مع أي طرف، مستشارون مرخّصون فقط." : "We never call twice, never share your number, RERA-licensed advisors only."}
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer select-none pt-2">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="cf-agree"
                  className="mt-0.5 w-4 h-4 shrink-0 accent-propertic-gold cursor-pointer"
                />
                <span className="text-[12px] text-propertic-ink-2/95 leading-[1.5]">
                  {t("consultation.agree_prefix")}{" "}
                  <Link to={lh("/terms")} className="text-propertic-gold underline hover:text-propertic-gold-2" data-testid="cf-terms-link">{t("consultation.agree_terms")}</Link>
                  {" "}{t("consultation.agree_and")}{" "}
                  <Link to={lh("/privacy")} className="text-propertic-gold underline hover:text-propertic-gold-2" data-testid="cf-privacy-link">{t("consultation.agree_privacy")}</Link>.
                </span>
              </label>

              {/* Progress ring around the submit button, gold fill increases
                  as the form completes (name → phone → terms). Gamifies the
                  final push to submit. */}
              <div className="relative mt-3">
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox="0 0 400 48"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <rect
                    x="1" y="1" width="398" height="46" rx="23"
                    fill="none"
                    stroke="#D5AB63"
                    strokeWidth="2"
                    strokeDasharray="888"
                    strokeDashoffset={888 * (1 - progress)}
                    style={{ transition: "stroke-dashoffset 0.4s ease" }}
                    data-testid="cf-progress-ring"
                  />
                </svg>
                <button
                  type="submit"
                  disabled={sending || !agreed}
                  data-testid="cf-submit"
                  className="relative bg-propertic-navy text-propertic-paper rounded-full w-full h-12 font-mono-brand text-[11px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-3 hover:bg-propertic-navy-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? "…" : t("consultation.submit")}
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
